import React, { useState, useEffect } from "react";
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { API_URL } from '../apiConfig';
import { useSelector } from "react-redux";

import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import CustomTypography from './UI/Typography/CustomTypography';
import ActionsComplexCard from './UI/Cards/ActionsComplexCard';
import CustomPager from './UI/Pagination/CustomPager';
import { Typography, Button } from '@mui/material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  shape: {
    borderRadius: 5
  },
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#F9F9F9',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,

  display: 'flex', // Make it a flex container
  flexDirection: 'column', // Align children in a column
  alignItems: 'center', // Center items horizontally
  justifyContent: 'center', // Center items vertically
}));

const Actions = () => {
  const [latestActions, setLatestActions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;
  const currentUser = useSelector((state) => state.auth.user);
  const [actionsIds, setActionsIds] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedActions = latestActions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  //Fetch ONLY the ids of all actions to fill the pager 
  const fetchAllActionsIds = async () => {
    try {
      const response = await axios.get(API_URL + '/actions/ids');
      const idsArray = response.data.map(actionsItem => actionsItem.id);
      setActionsIds(idsArray);
      setTotalItems(idsArray.length);
    } catch (error) {
      console.error("Error fetching all actions IDs:", error);
      // Handle error if needed
    }
  };

  //Then get 4 Actions per page 
  const fetchActions = async () => {
    try {
      const response = await axios.get(API_URL + '/actions/paged', {
        params: {
          page: currentPage,
          itemsPerPage: itemsPerPage,
        },
      });

      // Access the 'rows' property in the response
      const actionsArray = response.data.rows.map((actionsItem) => ({
        id: actionsItem.id,
        title: actionsItem.title,
        shortDescription: actionsItem.shortDescription,
        date: new Date(actionsItem.updatedAt),
        pdf: actionsItem.pdf,
        pdfURL: actionsItem.pdfURL,
      }));

      // Sort actionsArray by ID in descending order
      const sortedActionsArray = actionsArray.sort((a, b) => b.id - a.id);
      // Get the latest actions items
      setLatestActions(sortedActionsArray);
    } catch (error) {
      console.error("Error fetching actions:", error);
      // Handle error if needed
    }
  };

  useEffect(() => {
    fetchAllActionsIds();
    fetchActions();
  }, [currentPage]); // Trigger fetchActions when currentPage changes

  useEffect(() => {
    // For example, you can console.log the latestActions to verify if it's updating correctly.
  }, [latestActions]);

  // Add use cases data
  const useCases = [
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture1.jpg`,
      title: 'Athens International Airport, Eleutherius Venizelos',
      description: `Illustrating the complex security challenges faced by transport hubs across Europe. Serving as a vital crossroad for over 28 million passengers in 2023, the airport connects Greece to both international and domestic destinations, reinforcing its economic and social significance. Airports, as mix of public spaces and critical infrastructures, are essential to be protected not only for safeguarding passengers, personnel, and operations but also for ensuring the stability of financial networks, public trust, and the smooth functioning of transportation systems across the continent.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture2.jpg`,
      title: 'Port of Thessaloniki',
      description: `A vibrant urban hub that combines both maritime commerce and public access, making it a focal point of cultural and economic activity. In addition to facilitating significant commercial operations, the port also serves as a popular destination for tourists and locals, with public areas featuring shops, cafes, bars, and venues for live events, such as music performances. This blend of tourism, entertainment, and commercial trade created distinct logistical and security challenges.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture3.jpg`,
      title: 'Vieux Lyon (Old Lyon) and its pedestrian district',
      description: `One of Europe's most distinguished UNESCO World Heritage Sites, attracting numerous tourists annually. Renowned for its Renaissance architecture, cobblestone streets, and pedestrian-friendly areas, this historic district is a prime example of heritage tourism. As a UNESCO-listed site, it carries immense cultural and historical significance, requiring dedicated efforts to protect both its architectural integrity and the safety of its visitors. The preservation of Vieux Lyon's unique character is paramount, ensuring that its rich history remains intact while also accommodating the increasing number of visitors it attracts each year.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture4.jpg`,
      title: 'Mykonos Town (Chora)',
      description: `A world-renowned destination, attracting over 2 million visitors annually. Famous for its upscale luxury, vibrant nightlife, and its status as a high-end tourist hotspot, Chora offers a diverse range of activities that cater to all types of travellers. From its luxurious resorts and trendy nightclubs to its bustling shops, restaurants, and cultural experiences, Mykonos embodies the full spectrum of tourism, making it a prime location for both leisure and high-profile visitors. The increased foot traffic and lively atmosphere create specific challenges in managing public safety and security.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture5.jpg`,
      title: 'The Port of Varna',
      description: `A key commercial hub and one of Eastern Europe's emerging seaside tourist destinations. This dual function is essential for business and leisure travellers and cruise passengers exploring the region. The combination of industrial areas and functions with public access areas—such as waterfront promenades, dining and event venues, and accommodation—underscores the port's importance as a multifunctional urban area.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture6.jpg`,
      title: 'Thessaloniki Monasteriotes Synagogue',
      description: `Thessaloniki's Jewish community has a deep-rooted history that spans centuries, and the Monasteriotes' Synagogue remains one of its most important historical and religious monuments. While the synagogue is not frequently used for regular worship, it serves as a place of reflection and commemoration during special occasions, including visits from high-profile individuals and significant cultural or religious events. Due to its historical and symbolic value, the synagogue is vulnerable to various security risks, including vandalism, hate crimes, or politically motivated attacks.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture7.jpg`,
      title: 'Limassol Marina',
      description: `A vibrant Mediterranean hub that seamlessly combines luxury residences, fine dining, and maritime activities, attracting affluent leisure tourists, yacht owners, and business travellers. The marina's unique blend of upscale residential living, commercial offerings, and public-access waterfront areas makes it a focal point for a diverse range of visitors. This dynamic environment presents distinct security challenges, particularly given the high-profile nature of the area and the range of activities taking place.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture8.jpg`,
      title: 'The Royal Apollonia Hotel, Limassol',
      description: `Located in Limassol, Cyprus, this prestigious five-star establishment is renowned for its luxurious beachfront location and comprehensive amenities. The hotel caters to a broad spectrum of guests, including leisure tourists, business travellers, and attendees of conferences and events. Its diverse clientele and multifunctional spaces present a set of distinct security challenges. Moreover, the open-access nature of specific hotel spaces, combined with its prime location in a popular urban area, makes it susceptible to external threats, such as unauthorized access and public disturbances.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture9.jpg`,
      title: "Kraków's Christmas Market",
      description: `Krakow's Main Square a UNESCO World Heritage site, becomes the centre of the city's winter festivities during Christmas, attracting millions of local and international visitors. Known for its picturesque medieval architecture and festive atmosphere, the square hosts one of Europe's most iconic Christmas markets, which showcases local traditions, arts, and food. However, the site's open-air design, historical significance, and high foot traffic present specific security challenges requiring careful consideration.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture10.jpg`,
      title: 'Acropolis archaeological surrounding: The Odeon of Herodes Atticus, Greece',
      description: `The Odeon of Herodes Atticus, situated at the base of the Acropolis in Athens, is a culturally and historically significant site, attracting thousands of visitors annually worldwide. The site faces unique security challenges as a venue for large-scale performances and a landmark in Greece's ancient heritage. While the amphitheatre is a central attraction, the surrounding area, especially the spaces leading to the entrance, is frequently congested, further complicating safety and security measures.`
    },
  ];

  // Add critical incidents data
  const criticalIncidents = [
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture11.jpg`,
      title: 'Paris attacks (November 13, 2015)',
      description: `The 2015 Paris attacks occurred on the evening of 13 November 2015, when a group of terrorists affiliated with the Islamic State of Iraq and the Levant (ISIL) launched a coordinated assault across multiple locations in Paris, France. The terrorists selected locations with large congregations of people, including restaurants, bars, the Bataclan theatre, and the Stade de France, as their targets. The attacks commenced with suicide bombings at the stadium and subsequently progressed to mass shootings at other locations. A total of 130 individuals were killed, with hundreds more sustaining injuries. This event left an indelible mark on France and the global fight against terrorism, as the city was brought to a standstill in the face of such a large-scale assault. The attacks were widely condemned internationally, marking a pivotal moment in the ongoing struggle against violent extremism.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture12.jpg`,
      title: 'Brussels bombings (March 22, 2016)',
      description: `The 2016 Brussels bombings, occurred on 22 March and targeted major transportation hubs in Belgium's capital city. Two coordinated attacks were perpetrated: one at Brussels Airport in Zaventem and the other at Maalbeek metro station in the vicinity of European Union institutions. The explosions constituted part of a broader series of ISIS-linked terrorist attacks in Europe, which commenced with the Paris attacks in November 2015. The bombings caused significant disruption due to the fact that they occurred during the busy morning rush hour. The previous, resulted in the implementation of a lockdown of transportation networks and emergency operations across Belgium. The incidents demonstrated deficiencies in the country's security apparatus and underscored the increasing prevalence of extremist violence in Europe.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture13.jpg`,
      title: 'Nice truck attack (July 14, 2016)',
      description: `On 14 July 2016, a terrorist attack with hostile vehicle occurred in Nice, France, during the city's annual Bastille Day celebrations. As thousands of people gathered along the Promenade des Anglais to enjoy the fireworks display, a large truck was deliberately driven into the crowd, resulting in a horrific scene of chaos and destruction. The attack resulted in the deaths of 86 individuals and left over 400 injured, making it one of the deadliest terrorist incidents in France since the November 2015 Paris attacks. The incident shocked not only France, but also the entire world, highlighting vulnerabilities in public safety and security during large gatherings.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture14.jpg`,
      title: 'Berlin Christmas Market Attack (December 19, 2016)',
      description: `The 2016 Berlin Christmas Market Attack, was a notable act of terrorism that occurred on 19 December 2016 in Berlin, Germany. A vehicle was deliberately driven into a crowded Christmas market at Breitscheidplatz, a popular holiday destination in close proximity to the Kaiser Wilhelm Memorial Church. The incident occurred in the late afternoon, when the market was densely crowded, with a large number of people present to enjoy the festivities. The attack prompted a re-evaluation of the evolving threat of terrorism, particularly in public spaces, across Europe. The perpetrator selected a large group of civilians as the target of the attack, with the intention of causing mass casualties. This incident follows a disturbing trend of vehicle-ramming attacks that have taken place in various cities worldwide.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture15.jpg`,
      title: 'Las Vegas shooting (October 1, 2017)',
      description: `On 1 October 2017, a catastrophic mass shooting occurred at the Route 91 Harvest Festival, a three-day country music event held on the Las Vegas Strip in Nevada. The attack, regarded as one of the deadliest in modern U.S. history, was perpetrated by a lone gunman who opened fire on the crowd from the 32nd floor of the Mandalay Bay Hotel. The assault commenced at 10:05 p.m. and continued for approximately ten minutes, during which the assailant discharged hundreds of rounds into the densely packed audience of over 22,000 individuals without any discernible pattern or discrimination. The incident resulted in a significant loss of life, extensive injuries, and widespread panic, thereby fundamentally altering perceptions of safety at large public gatherings.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture16.jpg`,
      title: 'Reina nightclub shooting, Istanbul (January 1, 2017)',
      description: `The Reina nightclub shooting in Istanbul occurred on 1 January 2017, α gunman opened fire at a popular nightclub in the Beşiktaş district of Istanbul, Turkey. The attack took place during the New Year's Eve celebrations, targeting hundreds of individuals who had gathered to mark the beginning of the New Year. The Reina nightclub, situated along the Bosporus, was a prominent establishment that attracted a diverse clientele, including Turkish nationals and international tourists. At approximately 1:15 a.m., the perpetrator gained access to the establishment and commenced firing indiscriminately, resulting in a mass casualty incident. The attack was subsequently claimed by the Islamic State (ISIS), as part of their ongoing campaign of terrorism in Turkey and abroad.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture17.jpg`,
      title: 'Manchester Arena attack (May 22, 2017)',
      description: `The Manchester Arena attack occurred on 22 May 2017, subsequent to a concert by the pop singer Ariana Grande. As attendees began to evacuate the venue, a suicide bomber detonated an explosive device in the foyer area, resulting in significant damage and casualties. The explosion occurred at 10:31 p.m. local time, resulting in widespread panic and a chaotic scene as thousands of individuals attempted to flee the arena. The attack caused shockwaves throughout the UK and the wider world, as it targeted a public event filled with young fans, leaving behind a grim mark on a night that was meant to be one of joy and entertainment.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture18.jpg`,
      title: '2017 London Bridge attack (June 3, 2017)',
      description: `The 2017 London Bridge terrorist attack occurred on 3 June 2017, when three assailants executed a coordinated assault in central London. The incident commenced with the perpetrators driving a vehicle into pedestrians on London Bridge, resulting in numerous casualties. Subsequently, the assailants exited the vehicle and commenced stabbing bystanders in the congested market area. The use of fake explosive vests by the terrorists contributed to the heightened chaos and the extensive fear, experienced by civilians. The attack lasted approximately eight minutes, before armed police shot the terrorists. The Islamic State in Iraq and Syria (ISIS) subsequently claimed responsibility for this atrocity. In total, eight victims and the three attackers lost their lives, while at least 48 individuals sustained injuries.`
    },
    {
      image: `${process.env.PUBLIC_URL}/images/actions/Picture19.jpg`,
      title: 'Tree of Life Synagogue shooting, Pittsburgh (October 27, 2018)',
      description: `On 27 October 2018, a mass shooting occurred at the Tree of Life Synagogue in Pittsburgh, Pennsylvania, during the morning service held in observance of the Jewish Sabbath. This calamitous event, widely regarded as the deadliest attack on the Jewish community in U.S. history, resulted in the death of 11 individuals and injuries to several others. The perpetrator, armed with multiple firearms, proceeded to enter the synagogue and opened fire on the worshippers who were gathered for prayer. The attack sent shockwaves through the nation, drawing attention to the enduring problem of anti-Semitism and hate-motivated violence in the United States. The authorities responded expeditiously to the scene, and following a gunfight, the perpetrator was apprehended.`
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xxl">
        <Box sx={{ flexGrow: 1, bgcolor: 'white' }}>

          {/* Actions List Header */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Item 
                style={{ 
                  paddingTop: '130px', 
                  paddingBottom: '130px', 
                  minHeight: '400px', // Ensure the Item has a minimum height
                  backgroundImage: `url(${process.env.PUBLIC_URL}/images/BannerImages/actions.JPG)`, 
                  backgroundPosition: '0% 20%', // Pan the image down by 20%
                  backgroundSize: 'cover', // Ensure the image covers the entire area
                  backgroundRepeat: 'no-repeat' // Prevent the image from repeating
                }}
              >
              </Item>
            </Grid>
          </Grid>

          {/* Actions List Section */}
          <Grid container spacing={2} style={{ marginBottom: '25px', marginTop: '25px' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <CustomTypography variant="h3" text="Actions List" color="#0651A0" underlineThickness={3} underline={true} />
              </Box>
            </Grid>
          </Grid>

          {/* New Text Sections */}
          <Grid container spacing={4} style={{ marginBottom: '40px' }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} style={{ padding: '20px', height: '100%' }}>
                <Typography variant="h5" component="h2" style={{ marginBottom: '15px', color: '#0651A0', textAlign: 'center' }}>
                  Vulnerability Assessments
                </Typography>
                <Typography variant="body1" style={{ lineHeight: '1.6', textAlign: 'center' }}>
                  As part of the HECTOR Project, on-site vulnerability assessments were carried out at ten
                  tourist locations, representing a broad spectrum of tourism activities. The primary goal was
                  to identify potential weaknesses and propose measures to eliminate them, helping to
                  prevent future crises. A secondary objective was to highlight the unique challenges faced by
                  each type of site and to identify best practices. These insights will inform the next phase of
                  the project, supporting the creation of a unified and highly effective response mechanism
                  that ensures rapid, coordinated, and efficient action during times of crisis.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} style={{ padding: '20px', height: '100%' }}>
                <Typography variant="h5" component="h2" style={{ marginBottom: '15px', color: '#0651A0', textAlign: 'center' }}>
                  Critical Incidents Observatory
                </Typography>
                <Typography variant="body1" style={{ lineHeight: '1.6', textAlign: 'center' }}>
                  The HECTOR Project is dedicated to strengthening security at tourist destinations through
                  innovative initiatives. One of its key actions is the Critical Incidents Observatory (CIO), which
                  gathers and analyzes data on security incidents, such as terrorist attacks at tourist sites. By
                  doing so, the CIO develops tailored procedures and recommendations to improve
                  emergency response. The goal is to ensure that public space operators and security
                  personnel are well-prepared to handle any critical situation effectively.
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Side-by-side Use Cases Grids */}
          <Grid container spacing={4} sx={{ mb: 6, mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                {useCases.map((uc, idx) => (
                  <Grid item xs={12} sm={12} key={idx}>
                    <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img
                          src={uc.image}
                          alt={uc.title}
                          style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }}
                        />
                      </Box>
                      <Typography variant="h6" align="center" sx={{ fontWeight: 500, mb: 1 }}>
                        {uc.title}
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
                        {uc.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                {criticalIncidents.map((ci, idx) => (
                  <Grid item xs={12} sm={12} key={idx}>
                    <Paper elevation={3} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <img
                          src={ci.image}
                          alt={ci.title}
                          style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }}
                        />
                      </Box>
                      <Typography variant="h6" align="center" sx={{ fontWeight: 500, mb: 1 }}>
                        {ci.title}
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
                        {ci.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ paddingTop: '50px', justifyContent: 'center', alignItems: 'center' }}>
            {latestActions.map((actionsItem, index) => (
              <Grid item xs={12} sm={12} md={10} lg={10} key={index} style={{ marginBottom: '20px' }}>
                <Paper elevation={3} style={{ display: 'flex', padding: '20px', borderRadius: '8px' }}>
                  <Box style={{ width: '200px', height: '200px', marginRight: '20px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img 
                      src={actionsItem.pdfURL} 
                      alt={actionsItem.title}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }} 
                    />
                  </Box>
                  <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h5" component="h2" style={{ marginBottom: '10px' }}>
                        {actionsItem.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '10px' }}>
                        {actionsItem.date.toLocaleDateString('el-GR', {
                    day: 'numeric',
                    month: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric'
                  })}
                      </Typography>
                      <Typography variant="body1" style={{ marginBottom: '15px' }}>
                        {actionsItem.shortDescription}
                      </Typography>
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Button 
                          component={Link} 
                          to={`/actions/${actionsItem.id}`}
                          variant="contained" 
                          color="primary"
                          style={{ marginRight: '10px' }}
                        >
                          Learn More
                        </Button>
                        <Button 
                          variant="outlined" 
                          color="primary"
                        >
                          Share
                        </Button>
                      </Box>
                      <Button 
                        variant="contained" 
                        color="secondary"
                        component="a"
                        href={actionsItem.pdfURL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View PDF
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingTop: '50px' }}>
              <CustomPager
                pageCount={Math.ceil(totalItems / itemsPerPage)}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </Grid>
          </Grid>

          {/* Footer Section */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12} sm={12} md={12}>
                <Item style={{ paddingTop: '50px', paddingBottom: '50px', backgroundImage: `url(${process.env.PUBLIC_URL}/images/BannerImages/footer.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" padding="20px">
                  <Box display="flex" alignItems="center">
                    <img src={`${process.env.PUBLIC_URL}/images/eu-flag.png`} alt="European Union Flag" style={{ width: '100px', height: 'auto', marginRight: '10px' }} />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" flex="1">
                    <CustomTypography variant="h7" text="This project is funded by the European Union's Internal Security Fund — Police." color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" flex="1">
                    <CustomTypography variant="h7" text="Grant Agreement No. 101100558 — PROJECT HECTOR" color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" flex="1">
                    <CustomTypography variant="h7" text="The content of this webpage represents the views of the author only and is his/her sole responsibility. The European Commission does not accept any responsibility for use that may be made of the information it contains." color="white" />
                  </Box>
                </Box>
              </Item>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </ThemeProvider >
  );
};

export default Actions;
